import React from "react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";
import { formatPrice } from "@/src/utils/formatPrice";

import {
  transactionEmailColumn,
  transactionNameColumn,
  transactionOfferColumn,
  transactionPaymentColumn,
} from "./transactionColumns";
import { getSubscriptionPayment } from "../../../../utils/data/Payment";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import { dateColumn, statusColumn } from "../../../dataTable/dataTableColumns";
import { SUBSCRIPTION_STATUS_TONES } from "../../../offers/tableParams/offerUserColumns";

import type { TFunction } from "i18next";

import type { ActiveSubscriberRow } from "./activeSubscriberColumns.types";
import type { ActiveSubscription } from "../../../../api/StatsAPI.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface ActiveSubscriberColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
  userAbility: boolean;
  offerAbility: boolean;
  hasPaypalProvider: boolean;
}

// Regroupés sous `error` ; le statut brut reste dans `status_detail`.
const ERROR_STATUS_LIST = ["incomplete", "incomplete_expired", "past_due"];

const STATUS_LIST = [
  "active",
  "trialing",
  "processing",
  "waiting_iap_validation",
  "canceled",
  "ended",
  "unknown",
  "unpaid",
  "error",
] as const;

function getFrequencyLabel(
  subscription: ActiveSubscription,
  t: TFunction,
): string {
  if (subscription.frequency > 0) {
    return t("month", { count: subscription.frequency });
  }
  if (subscription.payment_processor.startsWith("stripe_")) {
    return t("purchase_type.gift");
  }
  return t("free_copy");
}

export function activeSubscriberRow(
  subscription: ActiveSubscription,
  t: TFunction,
): ActiveSubscriberRow {
  const { user, offer, price, currency } = subscription;
  const priceLabel = formatPrice(price, currency);

  return {
    uuid: subscription.uuid,
    user_uuid: subscription.user_uuid,
    offer_uuid: subscription.offer_uuid,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    payment: getSubscriptionPayment(subscription),
    retailer_name: subscription.retailer_name,
    price_label: priceLabel ?? "",
    price: priceLabel ? Number(price) : null,
    offer_label: offer.label,
    subscription_status: ERROR_STATUS_LIST.includes(subscription.status)
      ? "error"
      : subscription.status,
    status_detail: subscription.status,
    frequency: subscription.frequency,
    frequency_label: getFrequencyLabel(subscription, t),
    subscription_date: subscription.subscription_date,
    next_billing_date: subscription.next_billing_date,
  };
}

export { getTransactionUserHref as getActiveSubscriberHref } from "./transactionColumns";

export function activeSubscriberColumns({
  t,
  projectSlug,
  userAbility,
  offerAbility,
  hasPaypalProvider,
}: ActiveSubscriberColumnsOptions): DataTableColumnDef<ActiveSubscriberRow>[] {
  return [
    transactionEmailColumn<ActiveSubscriberRow>(t, projectSlug, userAbility, {
      minWidth: 150,
    }),
    transactionNameColumn<ActiveSubscriberRow>(t, { minWidth: 150 }),
    {
      ...transactionPaymentColumn<ActiveSubscriberRow>(t, hasPaypalProvider),
      enableHiding: true,
    },
    {
      // L'identifiant reste la clé d'URL ; le tri porte sur le montant, pas sur le texte.
      id: "price_label",
      accessorFn: (row) => row.price,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      meta: {
        label: t("price"),
        width: "9%",
        minWidth: 105,
      },
      cell: ({ row }) => {
        const { price_label: priceLabel, payment } = row.original;
        if (!priceLabel) return null;

        return payment === "apple" ? (
          <span
            data-tooltip-content={t("go_to_apple_store")}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            {priceLabel}
          </span>
        ) : (
          priceLabel
        );
      },
    },
    transactionOfferColumn<ActiveSubscriberRow>(
      t("offer"),
      projectSlug,
      offerAbility,
      { minWidth: 150 },
    ),
    statusColumn<ActiveSubscriberRow>(
      "subscription_status",
      t("offer_status"),
      STATUS_LIST.map((value) => ({
        value,
        label: t(`subscription_status.${value}`),
        tone: SUBSCRIPTION_STATUS_TONES[value],
      })),
      undefined,
      ({ subscription_status: status, status_detail: detail }) =>
        status === "error"
          ? [t("subscription_status.error"), t(`subscription_status.${detail}`)]
          : undefined,
    ),
    {
      id: "frequency",
      // Le tri porte sur le nombre de mois, pas sur le libellé.
      accessorFn: (row) => row.frequency,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("frequency")} />
      ),
      meta: { label: t("frequency"), width: "11%", minWidth: 135 },
      cell: ({ row }) => row.original.frequency_label,
    },
    {
      ...dateColumn<ActiveSubscriberRow>(
        "subscription_date",
        t("first_payment"),
        { width: "14%", minWidth: 175 },
      ),
      enableHiding: true,
    },
    {
      ...dateColumn<ActiveSubscriberRow>(
        "next_billing_date",
        t("next_billing"),
        { width: "16%", minWidth: 195 },
      ),
      enableHiding: true,
    },
  ];
}
